import { memo } from 'react';
import { Typography, Box, useTheme } from '@mui/material';
import { IconHelper } from 'components/generic/IconHelper';
import { bloxColors, fontWeightLight } from 'themes/constants';
import { summaryCardHeightPx } from './SummaryCard';
import { useTranslation } from 'react-i18next';

type PreviewImageProps = {
  previewUrl: string | undefined;
  title: string;
  type: string;
  previewWidth?: string;
  previewHeight?: string;
};

export const PreviewImage = memo(
  ({
    previewUrl,
    title,
    type,
    previewWidth = '172px',
    previewHeight = summaryCardHeightPx,
  }: PreviewImageProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    if (!previewUrl) {
      return (
        <Box
          id="summary-preview-image"
          data-testid="summary-preview-image"
          sx={{
            height: previewHeight,
            width: previewWidth,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === 'light'
                ? theme.palette.grey[300]
                : theme.palette.grey[700],
          }}
        >
          <Typography sx={{ fontSize: '1rem', fontWeightLight, color: bloxColors.gray3 }}>
            {t('blox.general.no_preview')}
          </Typography>
          <IconHelper
            iconType={type}
            svgIconProps={{ fontSize: 'large', style: { fill: bloxColors.gray3 } }}
          />
        </Box>
      );
    }

    return (
      <Box
        id="summary-preview-image"
        data-testid="summary-preview-image"
        sx={{
          position: 'relative',
          width: previewWidth,
          height: previewHeight,
        }}
      >
        {/* Using a regular img tag for the preview image */}
        <img
          height={previewHeight}
          width={previewWidth}
          src={previewUrl}
          alt={title}
          loading="lazy"
          style={{
            width: previewWidth,
            height: previewHeight,
            objectFit: 'cover',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.grey[300]}`,
          }}
        />
      </Box>
    );
  },
);
PreviewImage.displayName = 'PreviewImage';
